/**
 * ProShip Direct v2 — activity log.
 *
 * Rules and couriers are both switched off to change where orders go, so both need the same
 * question answered: allocation changed on Tuesday — what was turned off on Monday, and by whom.
 * `createdBy` on a rule only says who wrote it, not who later disabled it.
 *
 * One component serves both screens, filtered by entity, because the columns are identical and a
 * second copy would drift.
 */
import React from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableHead, TableRow, Chip,
} from '@mui/material';
import MUIModal from '../../../common/MUIModal';
import { formatDate } from '../../../helpers/UtilityHelper';
import { SUBTLE, NAVY, SUCCESS, WARNING, BORDER, WHITE, PRIMARY_BLUE } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { RADIUS } from '../../../styles/foundation/spacing';
import { tHeadSx, tCellSx } from './stats';
import { useActivityLog } from '../data/hooks';

const subSx = { fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE, lineHeight: 1.5 };
const nameSx = { fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: NAVY };

const ACTION_COLOR = { ENABLED: SUCCESS, DISABLED: WARNING, CREATED: PRIMARY_BLUE };

const actionChipSx = (action) => {
  const color = ACTION_COLOR[action] || SUBTLE;
  return {
    fontFamily: FONT,
    fontSize: SIZE['4xs'],
    fontWeight: WEIGHT.semibold,
    height: 19,
    borderRadius: '4px',
    bgcolor: `${color}14`,
    color,
  };
};

export default function ActivityLogModal({ entity, title, onClose }) {
  const { entries } = useActivityLog(entity);

  return (
    <MUIModal open handleClose={onClose} maxWidth='sm' title={title}>
      <Box sx={{ pt: 0.5 }}>
        {entries.length === 0 ? (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <Typography sx={nameSx}>Nothing changed yet</Typography>
            <Typography sx={subSx}>
              Switching something on or off is recorded here with who did it and when.
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: RADIUS.md, overflowX: 'auto', bgcolor: WHITE }}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell sx={tHeadSx}>When</TableCell>
                    <TableCell sx={tHeadSx}>What</TableCell>
                    <TableCell sx={tHeadSx}>Change</TableCell>
                    <TableCell sx={tHeadSx}>By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map((entry) => (
                    <TableRow key={entry.id} hover>
                      <TableCell sx={tCellSx}>{formatDate(entry.at, 'dd MMM, HH:mm')}</TableCell>
                      <TableCell sx={{ ...tCellSx, whiteSpace: 'normal' }}>
                        <Typography sx={nameSx}>{entry.name}</Typography>
                        {entry.detail && <Typography sx={subSx}>{entry.detail}</Typography>}
                      </TableCell>
                      <TableCell sx={tCellSx}>
                        <Chip
                          size='small'
                          label={entry.action.charAt(0) + entry.action.slice(1).toLowerCase()}
                          sx={actionChipSx(entry.action)}
                        />
                      </TableCell>
                      <TableCell sx={{ ...tCellSx, color: SUBTLE }}>{entry.user}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            <Typography sx={{ ...subSx, mt: 1 }}>
              Newest first, last 200 changes. Held in this browser until the backend records it.
            </Typography>
          </>
        )}
      </Box>
    </MUIModal>
  );
}
